import { useState } from 'react';
import { COLORS } from '../lib/theme';

/**
 * The country dial codes offered by the phone picker — the single source for
 * both the team-member invite and the hunt-guest invite. These are the markets
 * the apps sell in, and the set + order mirror the mobile phone picker's
 * `allowedCountries` (Sweden first, the default). Keep the domain-side
 * `parseInviteInput` DIAL_CODES in step with this list (same codes), or a
 * country the picker offers will normalize against the wrong dial code.
 */
export const INVITE_COUNTRIES = [
  { code: 'SE', flag: '🇸🇪', dial: '+46' },
  { code: 'FI', flag: '🇫🇮', dial: '+358' },
  { code: 'NO', flag: '🇳🇴', dial: '+47' },
  { code: 'AT', flag: '🇦🇹', dial: '+43' },
  { code: 'BE', flag: '🇧🇪', dial: '+32' },
  { code: 'DK', flag: '🇩🇰', dial: '+45' },
  { code: 'EE', flag: '🇪🇪', dial: '+372' },
  { code: 'FR', flag: '🇫🇷', dial: '+33' },
  { code: 'DE', flag: '🇩🇪', dial: '+49' },
  { code: 'HU', flag: '🇭🇺', dial: '+36' },
  { code: 'IT', flag: '🇮🇹', dial: '+39' },
  { code: 'LU', flag: '🇱🇺', dial: '+352' },
  { code: 'NL', flag: '🇳🇱', dial: '+31' },
  { code: 'PL', flag: '🇵🇱', dial: '+48' },
  { code: 'SK', flag: '🇸🇰', dial: '+421' },
  { code: 'ES', flag: '🇪🇸', dial: '+34' },
  { code: 'CH', flag: '🇨🇭', dial: '+41' },
  { code: 'UA', flag: '🇺🇦', dial: '+380' },
  { code: 'GB', flag: '🇬🇧', dial: '+44' },
];

/** The user-facing strings the modal renders — supplied by the caller so the
 * same component serves "invite a team member" and "invite a hunt guest". */
export interface InviteModalLabels {
  title: string;
  subtitle: string;
  email: string;
  orWith: string;
  number: string;
  add: string;
  sending: string;
  cancel: string;
}

export interface InviteByEmailOrPhoneModalProps {
  onClose: () => void;
  /**
   * Called with the raw fields when the user submits. Returns `{ ok }` to close,
   * or `{ ok: false, error }` to show the message and stay open. The email field
   * is passed through verbatim (it may hold several comma-separated addresses —
   * the caller decides whether to accept that).
   */
  onInvite?: (input: {
    email: string;
    phone: string;
    country: string;
  }) => Promise<{ ok: boolean; error?: string }>;
  labels: InviteModalLabels;
}

/**
 * One design-system modal for inviting by email OR phone: a floating-label
 * email field, an "or with" divider, and a country-dial-code picker + number
 * field, with the two sides mutually exclusive. Lifted out of `TeamDetailPage`
 * so team-member invites and hunt-guest invites (CLUB-972) render the one
 * component rather than two drifting copies.
 */
export function InviteByEmailOrPhoneModal({
  onClose,
  onInvite,
  labels,
}: InviteByEmailOrPhoneModalProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('SE');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailActive = email.length > 0;
  const phoneActive = phone.length > 0;
  const emailDisabled = phoneActive;
  const phoneDisabled = emailActive;

  const canSubmit = (email.trim() !== '' || phone.trim() !== '') && !submitting;

  const handleAdd = async () => {
    // No handler wired (e.g. storybook/preview) → preserve the old close behavior.
    if (!onInvite) { onClose(); return; }
    setSubmitting(true);
    setError(null);
    const result = await onInvite({ email, phone, country });
    setSubmitting(false);
    if (result.ok) onClose();
    else setError(result.error ?? 'Could not send the invite.');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,20,15,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div className="wh-card" style={{ width: 400, padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.onSurface, marginBottom: 6 }}>
          {labels.title}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 24 }}>
          {labels.subtitle}
        </div>

        {/* Email */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <label style={{
            position: 'absolute', top: email ? -9 : 12, left: 11,
            fontSize: email ? 11 : 14, color: emailDisabled ? COLORS.outlineVariant : email ? COLORS.primary : COLORS.outline,
            background: COLORS.surface, padding: '0 3px',
            transition: 'all 120ms', pointerEvents: 'none', zIndex: 1,
          }}>{labels.email}</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={emailDisabled}
            type="email"
            aria-label="Email"
            style={{
              width: '100%', height: 52, padding: '0 14px', borderRadius: 8, boxSizing: 'border-box',
              border: `1.5px solid ${emailDisabled ? COLORS.outlineVariant : email ? COLORS.primary : COLORS.outlineVariant}`,
              background: emailDisabled ? COLORS.surfaceContainerLowest : COLORS.surface,
              fontSize: 15, color: emailDisabled ? COLORS.outline : COLORS.onSurface,
              outline: 'none', opacity: emailDisabled ? 0.5 : 1, transition: 'border-color 120ms, opacity 120ms',
            }}
          />
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: COLORS.outlineVariant }} />
          <span style={{ fontSize: 13, color: COLORS.outline, flexShrink: 0 }}>{labels.orWith}</span>
          <div style={{ flex: 1, height: 1, background: COLORS.outlineVariant }} />
        </div>

        {/* Phone */}
        <div style={{ display: 'flex', gap: 8, opacity: phoneDisabled ? 0.5 : 1, transition: 'opacity 120ms' }}>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={phoneDisabled}
            aria-label="Country"
            style={{
              height: 52, padding: '0 10px', borderRadius: 8, flexShrink: 0,
              border: `1.5px solid ${phoneActive ? COLORS.primary : COLORS.outlineVariant}`,
              background: COLORS.surface, fontSize: 15, color: COLORS.onSurface,
              cursor: phoneDisabled ? 'not-allowed' : 'pointer', outline: 'none',
            }}
          >
            {INVITE_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
            ))}
          </select>
          <div style={{ position: 'relative', flex: 1 }}>
            <label style={{
              position: 'absolute', top: phone ? -9 : 14, left: 11,
              fontSize: phone ? 11 : 14, color: phoneDisabled ? COLORS.outlineVariant : phone ? COLORS.primary : COLORS.outline,
              background: COLORS.surface, padding: '0 3px',
              transition: 'all 120ms', pointerEvents: 'none', zIndex: 1,
            }}>{labels.number}</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={phoneDisabled}
              type="tel"
              aria-label="Phone number"
              style={{
                width: '100%', height: 52, padding: '0 14px', borderRadius: 8, boxSizing: 'border-box',
                border: `1.5px solid ${phone ? COLORS.primary : COLORS.outlineVariant}`,
                background: COLORS.surface, fontSize: 15, color: COLORS.onSurface,
                outline: 'none', cursor: phoneDisabled ? 'not-allowed' : 'text',
              }}
            />
          </div>
        </div>

        {error && (
          <div role="alert" style={{ marginTop: 16, fontSize: 13, color: COLORS.error }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 28 }}>
          <button className="wh-btn wh-btn-text" onClick={onClose} disabled={submitting}>{labels.cancel}</button>
          <button className="wh-btn wh-btn-outline" disabled={!canSubmit} onClick={handleAdd}>
            {submitting ? labels.sending : labels.add}
          </button>
        </div>
      </div>
    </div>
  );
}
